import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { PriceChangeRuleDetails } from '../../../common/types/rates.types';
import PriceChangeForm from './PriceChangeForm';
import {
  getDefaultRatePriceChangeRuleAsync,
  selectPriceChangeRuleDetails,
  selectPriceChangeRuleDetailsLoadingStatus,
  updateDefaultRatePriceChangeRuleAsync,
} from '../defaultRateRulesSlice';
import {
  copyPriceChangeRuleToSeasons,
  deleteRulesFromSeasonsAsync,
} from '../../defaultRateTools/defaultRateToolsSlice';
import SeasonRatePickerContainer from '../../seasonRates/SeasonRatePickerContainer';
import { SEASON_RATE_RULE_TYPE } from '../../../common/constants';

function DefaultRatePriceChange({
  defaultRateId,
  accommodationId,
  hasWriteAccess,
}: {
  defaultRateId: string;
  accommodationId: string;
  hasWriteAccess: boolean;
}) {
  const dispatch = useAppDispatch();
  const priceChangeRuleDetails = useAppSelector(selectPriceChangeRuleDetails);
  const priceChangeStatus = useAppSelector(
    selectPriceChangeRuleDetailsLoadingStatus,
  );
  const [selectedSeasonRateIds, setSelectedSeasonRateIds] = useState<string[]>(
    [],
  );

  const initialData: PriceChangeRuleDetails = {
    defaultRateId,
    priceChange: {
      priceChangeType: 0,
      percent: 0,
      difference: 0,
    },
    isActive: true,
    isCopyable: true,
  };

  const [data, setData] = useState(initialData);
  const [submitted, setSubmitted] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [copied, setCopied] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getDefaultRatePriceChangeRuleAsync(defaultRateId));
  }, [defaultRateId, dispatch]);

  useEffect(() => {
    if (priceChangeRuleDetails && priceChangeRuleDetails.id) {
      setData(priceChangeRuleDetails);
    } else {
      setData(initialData);
    }
  }, [priceChangeRuleDetails]);

  const handleInputChange = event => {
    const path = event.target.name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;
    setData(newData);
  };

  const handleSelectChange = (selectedOption, name) => {
    const path = name.split('.');
    const finalProp = path.pop();
    const newData = { ...data };
    let pointer = newData;
    path.forEach(el => {
      pointer[el] = { ...pointer[el] };
      pointer = pointer[el];
    });
    pointer[finalProp] = selectedOption.value;
    setData(newData);
  };

  const onSubmit = () => {
    dispatch(updateDefaultRatePriceChangeRuleAsync(data));
    setSubmitted(true);
  };

  const onDelete = () => {
    if (
      window.confirm('Haluatko varmasti poistaa säännöt valituilta kausilta?')
    ) {
      dispatch(
        deleteRulesFromSeasonsAsync({
          accommodationId,
          selectedSeasonRateIds,
          ruleType: SEASON_RATE_RULE_TYPE.SeasonRatePriceChangeRule,
        }),
      );
      setSubmitted(false);
      setData(initialData);
      setDeleted(true);
    }
  };

  const handleCopyingForSeasonRates = async () => {
    if (selectedSeasonRateIds.length > 0) {
      dispatch(
        copyPriceChangeRuleToSeasons({
          accommodationId,
          selectedSeasonRateIds,
          priceChange: {
            priceChangeType: data.priceChange.priceChangeType,
            percent: data.priceChange.percent,
            difference: data.priceChange.difference,
          },
        }),
      )
        .unwrap()
        .then(() => {
          setCopied(true);
        });
    }
  };

  const handleSeasonRateIds = (selectedIds: string[]) => {
    setSelectedSeasonRateIds(selectedIds);
  };

  return (
    <>
      <Row sm={1}>
        <Row sm={1}>
          <Col>
            <Form.Label>Valitse kohdekaudet</Form.Label>
          </Col>
        </Row>
        <Col>
          <SeasonRatePickerContainer
            selectedAccommodationId={accommodationId}
            onSelectedIdsChange={handleSeasonRateIds}
          />
        </Col>
      </Row>
      <PriceChangeForm
        loadingStatus={priceChangeStatus}
        handleInputChange={handleInputChange}
        handleSelectChange={handleSelectChange}
        onSubmit={onSubmit}
        onDelete={onDelete}
        submitted={submitted}
        deleted={deleted}
        data={data}
        hasWriteAccess={hasWriteAccess}
        isDefaultRate
      />
      <Form.Group className="mb-24">
        <Button
          variant="outline-blue"
          className="mt-16 mb-16 w-100"
          onClick={handleCopyingForSeasonRates}
        >
          Kopioi sääntö hintakausille
        </Button>
        {copied && (
          <div className="mt-2 pt-2">
            <Alert variant="success">Sääntö kopioitu hintakausille!</Alert>
          </div>
        )}
      </Form.Group>
    </>
  );
}

export default DefaultRatePriceChange;
